import React from 'react';
import { PacmanGameState } from '../PacmanGame';

interface GameStateOverlayProps {
    gameState: PacmanGameState;
    gameStarted: boolean;
    score: number;
    level: number;
    onStartGame: () => void;
    onResetGame: () => void;
    onStartNextLevel: () => void;
}

const GameStateOverlay: React.FC<GameStateOverlayProps> = ({
    gameState,
    gameStarted,
    score,
    level,
    onStartGame,
    onResetGame,
    onStartNextLevel
}) => {
    if (gameStarted && gameState === PacmanGameState.Playing) {
        return null; // No overlay when playing
    }

    const getOverlayContent = () => {
        if (!gameStarted) {
            return {
                title: '🏜️ SAHARA DOTMAN 🏜️',
                subtitle: 'Embark on a desert adventure!',
                color: '#FFD700',
                buttonText: '🚀 START ADVENTURE',
                buttonColor: '#FF6B35',
                onButtonClick: onStartGame,
                icon: '🐪'
            };
        }

        switch (gameState) {
            case PacmanGameState.LevelCompleteScreen:
                return {
                    title: '🌟 LEVEL COMPLETE! 🌟',
                    subtitle: `Desert Score: ${score.toLocaleString()} | Oasis Level: ${level}`,
                    color: '#32CD32',
                    buttonText: `🏜️ CONTINUE TO LEVEL ${level}`,
                    buttonColor: '#FFD700',
                    onButtonClick: onStartNextLevel,
                    icon: '💎'
                };
            case PacmanGameState.GameOver:
                return {
                    title: '💀 GAME OVER 💀',
                    subtitle: `Final Desert Score: ${score.toLocaleString()}`,
                    color: '#FF4500',
                    buttonText: '🔄 RESTART ADVENTURE',
                    buttonColor: '#FF6B35',
                    onButtonClick: onResetGame,
                    icon: '🦎'
                };
            case PacmanGameState.Paused:
                return {
                    title: '⏸️ PAUSED ⏸️',
                    subtitle: 'Take a break in the desert oasis',
                    color: '#FFD700',
                    buttonText: '▶️ RESUME ADVENTURE',
                    buttonColor: '#32CD32',
                    onButtonClick: onStartGame,
                    icon: '🌴'
                };
            default:
                return null;
        }
    };

    const content = getOverlayContent();
    if (!content) return null;

    return (
        <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(47,27,20,0.9) 0%,rgb(139, 69, 19) 50%, rgba(210,180,140,0.7) 100%)',
            padding: '15px',
            borderRadius: '10px',
            border: '2px solid #8b4513',
            boxShadow: '0 8px 25px rgba(0,0,0,0.5), 0 0 15px rgba(255,215,0,0.3)',
            minWidth: '180px',
            animation: 'overlayGlow 2s ease-in-out infinite alternate'
        }}>
            {/* Header */}
            <div style={{
                color: '#FFD700',
                fontWeight: 'bold',
                marginBottom: '10px',
                textAlign: 'center',
                fontSize: '16px',
                textShadow: '1px 1px 3px rgba(0,0,0,0.8)'
            }}>
                {content.icon} {content.title} {content.icon}
            </div>

            {/* Subtitle */}
            {content.subtitle && (
                <div style={{
                    color: '#F4E4BC',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    marginBottom: '15px',
                    padding: '5px',
                    background: 'rgba(255,215,0,0.1)',
                    borderRadius: '5px',
                    border: '1px solid rgba(255,215,0,0.3)'
                }}>
                    {content.subtitle}
                </div>
            )}

            {/* Button */}
            <button
                onClick={content.onButtonClick}
                style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    background: `linear-gradient(45deg, ${content.buttonColor}, #FFD700)`,
                    color: '#2F1B14',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease',
                    textShadow: '1px 1px 2px rgba(255,255,255,0.3)',
                    width: '100%'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.4)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                }}
            >
                {content.buttonText}
            </button>

            <style>{`
                @keyframes overlayGlow {
                    0% { box-shadow: 0 8px 25px rgba(0,0,0,0.5), 0 0 15px rgba(255,215,0,0.3); }
                    100% { box-shadow: 0 8px 25px rgba(0,0,0,0.5), 0 0 25px rgba(255,215,0,0.5); }
                }
            `}</style>
        </div>
    );
};

export default GameStateOverlay;